/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2016 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/

/**
 * Simple singleton style container so all Libray related stuff has a centralized point.
 */
var iaw = iaw || { };

iaw.libraryManager = {

	// global template lookup map
	statusLookupMap: {},
	hasSyncListener: false,

	// download status
	DOWNLOADED: 'downloaded',
	DOWNLOADING: 'downloading',
	DOWNLOAD_ERR: 'error',

	// template type and mime type
	TEMPLATE_ELEMENT_TYPE: 'application/vnd.adobe.element.template+dcx',
	MIME_TYPE: {
		'PHXS': 'image/vnd.adobe.photoshop.template',
		'ILST': 'application/illustrator.template',
		'IDSN': 'application/vnd.adobe.indesign.template'
	},

	// others
	initialized: false,
	libraryCollections: null,
	debounce: 300, // 0.3 second
	lastSyncTime: new Date().valueOf(),
	pendingSyncTimeout: null,
	checkRunningTimeout: null,
	cclibraryProcessLaunchPending: false, // CCLibrary process launch pending

	// cache licensed templates
	cachedLicensedTemplates: {},

	// __________________________________________________________________________
	/**
	 * Listener for library collections loaded
	 */
	onLibraryCollectionsLoaded: function() {
		clearTimeout(iaw.libraryManager.checkRunningTimeout);

		iaw.libraryManager.libraryCollections = ccLibraries && ccLibraries.getLoadedCollections();
		if (iaw.cepUtil.getExtensionID() !== 'com.adobe.ccx.fnft' || !iaw.libraryManager.libraryCollections) {
			return;
		}

		// Update status lookup map for fnft dialog
		iaw.libraryManager.getLicensedTemplates(iaw.store.get('host').hostID)
		.then(function(templates) {

			if (!window.__adobe_cep__) {
				iaw.fnftFakeLicensedTemplates.forEach(function(template) {
					iaw.libraryManager.statusLookupMap[template.id] = {
						templateId: template.id,
						downloadStatus: iaw.libraryManager.DOWNLOADED,
						path: template.url
					};
				});
				return;
			}

			if (!iaw.fnftFakeLicensedTemplates) {
				iaw.libraryManager.cachedLicensedTemplates = {};
			}

			templates.forEach(function(template) {
				iaw.libraryManager.statusLookupMap[template.id] = {
					templateId: template.id,
					downloadStatus: iaw.libraryManager.DOWNLOADED,
					path: template.url,
					elementRef: template.elementRef
				};
			});
		})
		.catch(function() {
			// ignore
		});

		//TODO: Enable this for ccx start screen when we need to detect cc lib changes there too.
		// Listen sync changes
		iaw.libraryManager.libraryCollections.forEach(function(libraryCollection) {
			(function(libraryCollection) {
				libraryCollection.addSyncListener(function() {
					iaw.libraryManager.onSync(libraryCollection);
				});
			})(libraryCollection);
		});
	},

	/**
	 * Load & init CC library
	 * @param filters 	an array of element filters, like ['application/vnd.adobe.element.template+dcx']
	 */
	init: function(filters) {
		filters = filters || '*';

		if (!iaw.libraryManager.isProcessInstalled()) {
			return iaw.log.console('CC Library Process is not installed.');
		}

		tophat(['cc-libraries-api.min.js'], function() {
			if (typeof ccLibraries === 'undefined') {
				return iaw.log.console('Fail to load CC Library javascript file.');
			}

			// Initialize ccLibraries
			var DEPENDENCIES = {
				log: function(message) {
					iaw.log.console(message);
				}
			};

			var OPTIONS = {
				SHARED_LOCAL_STORAGE: true,
				ELEMENT_TYPE_FILTERS: filters
			};

			ccLibraries.configure(DEPENDENCIES, OPTIONS);

			// VulcanInterface.isAppRunning is not reliable to know the running status. Sometimes it returns false for the running CC Lib Process on Mac.
			// We check if ccLibraries.isConnected in 1 second to determine the running status of CC Lib process. this workaround is suggested by CC Library team.
			iaw.libraryManager.checkRunningTimeout = setTimeout(function() {
				if ((!iaw.libraryManager.isProcessRunning()) && (!ccLibraries.isConnected())) {
					iaw.log.console('Start to launch CCLibrary Process');
					iaw.libraryManager.launchCCLibraryProcess();
				}
			}, 1000);

			ccLibraries.addLoadedCollectionsListener(iaw.libraryManager.onLibraryCollectionsLoaded);
		});

	},

    /**
	 * Get template library
	 *
	 * @return [Promise] promise that will get "Stock Templates" library
	 */
	getTemplateLibrary: function() {
		return new Promise(function(resolve, reject) {
			if (!iaw.libraryManager.isLibraryCollelctionsLoaded()) {
				iaw.log.console('Get template library: Fail to load library collections.');
				return reject();
			}

			var libraryCollection = iaw.libraryManager.libraryCollections[0];
			var templateLib;
			var libraryName = iaw.i18n.getLocalizedString('stock_template_library_name');
			libraryCollection.libraries.forEach(function(library) {
				if (!templateLib && library.name === libraryName) {
					templateLib = library;
				}
			});

			if (!templateLib) {
				templateLib = libraryCollection.createLibrary(libraryName);
			}
			resolve(templateLib);
		});
	},

	/**
	 * Util to check if representation is in downloading
	 * @param [Object] representation	CC library element's representation
	 * @return [Boolean] true or false
	 */
	isRepresentationDownloadPending: function(representation) {
		if (!representation.isExternalLink) {
			return false;
		}
		return !representation.getCachedContentPath() && !representation.getCachedExternalLinkError();
	},

	/**
	 * Util to check if element is in downloading
	 *
	 * @param [Object] element	CC library element
	 * @return [Boolean] true or false
	 */
	isElementDownloadPending: function(element) {
		return element.representations.some(function(representation) {
			return iaw.libraryManager.isRepresentationDownloadPending(representation);
		});
	},

	/**
	 * Util to retry downloading
	 *
	 * @param [Object] element	CC library element
	 */
	retryElementDownload: function(element) {
		element.representations.forEach(function(representation) {
			if (iaw.libraryManager.isRepresentationDownloadPending(representation)) {
				representation.getContentPath(); // This triggers the refetch
			}
		});
	},

	/**
	 * Util to get download progress
	 *
	 * @param element	CC library element
	 * @return [Number] download progress like 60
	 */
	getElementDownloadProgress: function(element) {
		var downloadProgress;
		element.representations.forEach(function(representation) {
			if (downloadProgress === undefined) {
				downloadProgress = representation.getExternalLinkDownloadProgress();
			}
		});
		return downloadProgress;
	},

	/**
	 * Hook CC library sync listener
	 */
	onSync: function(libraryCollection) {

		if (iaw.libraryManager.pendingSyncTimeout) {
			return;
		}

		var currentTime = new Date().valueOf();
		if (currentTime - iaw.libraryManager.lastSyncTime < iaw.libraryManager.debounce) {
			(function(libraryCollection) {
				iaw.libraryManager.pendingSyncTimeout = setTimeout(function() {
					iaw.libraryManager.pendingSyncTimeout = undefined;
					iaw.libraryManager.onSync(libraryCollection);
				}, iaw.libraryManager.debounce);
			})(libraryCollection);
			return;
		}

		iaw.libraryManager.lastSyncTime = currentTime;
		var statusLookupMap = iaw.libraryManager.statusLookupMap;

		libraryCollection.libraries.forEach(function(library) {
			library.getFilteredElements(iaw.libraryManager.TEMPLATE_ELEMENT_TYPE).forEach(function(element) {
				if (iaw.stockUtil.getElementStockLicense(element) === undefined) return;

				var id = iaw.stockUtil.getElementStockId(element);
				var status = statusLookupMap[id] || {templateId: id};
				var oldProgress = status.progress;
				status.progress = iaw.libraryManager.getElementDownloadProgress(element);
				var oldPending = status.downloadPending;
				status.downloadPending = iaw.libraryManager.isElementDownloadPending(element);

				var logPrefix = 'Stock template [' + status.templateId + '] ';
				// TODO: Split to onDownloaded, onDownloading, onDownloadError function
				if (status.downloadPending !== oldPending || status.progress !== oldProgress) {
					if (status.progress === undefined || (oldPending && !status.downloadPending)) {
						// downloaded
						// Filter out any odd progress event(s) from Library. Usually, it happens at parallel downloadings.
						if (oldProgress && status.progress && status.progress < oldProgress) return;

						element.getPrimaryRepresentation().getContentPath(function(err, path) {
							if (err || (status.downloadStatus === iaw.libraryManager.DOWNLOADED && status.path === path)) return;

							if (status.downloadStatus !== iaw.libraryManager.DOWNLOADED) {
								var templateData = iaw.util.getTemplateData(id) || {};
								iaw.analytics.ingest.logFNFTActionClickedEvent('download-end', templateData);
								iaw.log.console(logPrefix + 'is downloaded.');

								// update template data in data store.
								if (templateData) {
									templateData.licensedTime = element.modified;
									templateData.lastUsedTime = iaw.localstorage.getUserItem('templateLUT_' + id) || element.modified;
									templateData.template_category = templateData.template_category || [];
									if (templateData.template_category.indexOf('saved') === -1) {
										templateData.template_category.push('saved');
									}
								}
							}
							status.downloadStatus = iaw.libraryManager.DOWNLOADED;
							status.path = path;
							status.elementRef = element.getReference();
							iaw.store.set(['input', 'download-status'], status);
							statusLookupMap[id] = status;
						});
						return;
					}
					else if (status.progress === -1) {
						// download error
						if (status.downloadStatus === iaw.libraryManager.DOWNLOAD_ERR) return;
						status.downloadStatus = iaw.libraryManager.DOWNLOAD_ERR;
						iaw.log.console(logPrefix + 'fail to be downloaded.');
						iaw.store.set(['input', 'download-status'], status);
					}
					else if ((status.progress >= oldProgress) && status.downloadPending) {
						// downloading
						status.downloadStatus = iaw.libraryManager.DOWNLOADING;
						status.licensedTime = element.modified;
						status.lastUsedTime = element.modified;
						iaw.log.console(logPrefix + 'is being downloaded, progress=' + status.progress);
						iaw.store.set(['input', 'download-status'], status);
					}
					statusLookupMap[id] = status;
				}
				else if (status.downloadPending && (statusLookupMap[id].downloadStatus !== iaw.libraryManager.DOWNLOADING)) {
					iaw.analytics.ingest.logFNFTActionClickedEvent('download-start', iaw.util.getTemplateData(id) || {});
					status.downloadStatus = iaw.libraryManager.DOWNLOADING;
					status.progress = 0;
					statusLookupMap[id] = status;
					iaw.store.set(['input', 'download-status'], status);
				}
			});
		});
	},

	/**
	 * Build up template object based on CC library element
	 *
	 * @param [Object] element			cc library element
	 * @param [Boolean] checkPurchased	flag indicating to check purchase/license status or not
	 * @param [String] mime_type		mime type of template
	 * @return [Promise] promise that will build up template in desired format
	 */
	buildupTemplate: function(element, checkPurchased, mime_type) {

		return new Promise(function(resolve, reject) {
			if (!element || (checkPurchased && iaw.stockUtil.getElementStockLicense(element) === undefined) || iaw.libraryManager.isElementDownloadPending(element)) {
				return reject();
			}

			var representation = element.getPrimaryRepresentation();
			if (!representation) return reject();
			representation.getContentPath(function(err, templatePath) {
				if (err) return reject(err);
				element.getThumbnailPath(202, function(err, thumbnailPath) {
					if (err) {
						thumbnailPath = null;
					}
					var id = iaw.stockUtil.getElementStockId(element);
					resolve({
						id: id,
						mime_type: mime_type,
						template_category: ['saved', 'recent'],
						title: element.name,
						created: element.created,
						licensedTime: element.modified,
						modified: element.modified,
						lastUsedTime: iaw.localstorage.getUserItem('templateLUT_' + id) || element.modified,
						description: iaw.i18n.getLocalizedString('newdoc_details_template_default'),
						'thumbnail_url': thumbnailPath,
						width: representation.width,
						height: representation.height,
						url: templatePath,
						size: representation.contentLength,
						elementRef: element.getReference(),
						previews: []
					});
				});
			});
		});
	},

	/**
	 * Util to check if CC library is connected.
	 *
	 * @return [Boolean] true or false.
	 */
	isConnected: function() {
		return ccLibraries.isConnected();
	},

	/**
	 * Util to get CC library service info.
	 *
	 * @return [Object] service info.
	 */
	getServiceInfo: function() {
		return ccLibraries.getServiceInfo();
	},

	/**
	 * Util to check if CC library Process is installed
	 *
	 * @return [Boolean] true or false.
	 */
	isProcessInstalled: function() {
		return window.__adobe_cep__ ? VulcanInterface.isAppInstalled('cclibraries') : false;
	},


	// __________________________________________________________________________
	/**
	 * Public API
	 */

	/**
	 * Check if CC Library process is running or not.
	 *
	 * @return [Boolean] true or false.
	 */
	isProcessRunning: function() {
		return VulcanInterface.isAppRunning('cclibraries');
	},

	/**
	 * Launch CC library process.
	 */
	launchCCLibraryProcess: function() {
		if (iaw.libraryManager.cclibraryProcessLaunchPending) {
			return;
		}
		var interval;
		var message = 'vulcan.SuiteMessage.cclibraries.service.Initialized';

		function started() {
			if (interval) {
				clearInterval(interval);
			}
			iaw.libraryManager.cclibraryProcessLaunchPending = false;
			ccLibraries.reconnect();
			VulcanInterface.removeMessageListener(message, started);

			// Wait 1s, then check if we're still connected - if not, need to retry
			setTimeout(function() {
				if (!iaw.libraryManager.isConnected()) {
					iaw.libraryManager.launchCCLibraryProcess();
				}
			}, 1000);
		}

		function callLaunch() {
			// If we happen to already be connected then abort the interval
			if (iaw.libraryManager.isConnected()) {
				started();
				return;
			}

			// NOTE: We never try to launch the CC Library process if an update is required (either to the panel or process).
			// Unfortunately, Vulcan.isAppRunning always returns false on the Mac, which can mean we keep on trying to start the
			// process even though it's already running, and this causes 2 second hangs on the main thread in the desktop products.
			var serviceInfo = iaw.libraryManager.getServiceInfo() || {};
			if (!serviceInfo.updateRequired && iaw.libraryManager.isProcessInstalled() && !iaw.libraryManager.isProcessRunning()) {
				iaw.log.console('Launching CCLibrary Process');
				VulcanInterface.launchApp('cclibraries', false);
			}
		}

		VulcanInterface.addMessageListener(message, started);
		ccLibraries.reconnect();
		iaw.libraryManager.cclibraryProcessLaunchPending = true;

		interval = setInterval(callLaunch, 5000);
		callLaunch();
	},

	/**
	 * Get licensed templates per hostID.
	 *
	 * @param [String] hostID	host id, like 'PHXS'
	 * @return [Promise] promise that will resolve licensed templates
	 */
	getLicensedTemplates: function(hostID) {

		return new Promise(function(resolve, reject) {
			if (!window.__adobe_cep__) {
				return resolve(iaw.fnftFakeLicensedTemplates);
			}

			function onFinish(templates) {
				templates = templates.filter(function(item, pos) {
					return templates.indexOf(item) == pos;
				});

				resolve(templates);
			}

			if (!iaw.libraryManager.isLibraryCollelctionsLoaded()) {
				iaw.log.console('Get licensed templates: Fail to load library collections.');
				return reject();
			}

			var elements = [];
			var libraries = [];
			iaw.libraryManager.libraryCollections.forEach(function(libraryCollection) {
				libraries = libraries.concat(libraryCollection.libraries);
			});
			libraries.forEach(function(library) {
				elements = elements.concat(library.getFilteredElements(iaw.libraryManager.TEMPLATE_ELEMENT_TYPE));
			});

			// Filter out licensed templates via mime_type.
			// For Photoshop: it's application/photoshop.template
			// For Illustrator: it's application/illustrator.template'
			var mime_type = iaw.libraryManager.MIME_TYPE[hostID];
			if (mime_type) {
				elements = elements.filter(function(element) {
					return element.getPrimaryRepresentation().isCompatibleType(mime_type);
				});
			}

			var callCount = 0;
			var templates = [];
			if (!elements || elements.length === 0) {
				return onFinish([]);
			}

			// Build up the desired licensed template array.
			elements.forEach(function(element) {
				// build up template obj.
				iaw.libraryManager.buildupTemplate(element, true, mime_type)
				.then(function(template) {
					templates.push(template);
					if (++callCount === elements.length) {
						onFinish(templates);
					}
				})
				.catch(function() {
					if (++callCount === elements.length) {
						onFinish(templates);
					}
				});
			});
		});
	},

	/**
	 * Get templates per elements reference.
	 *
	 * @param [Array] elementRefs	an array of elements reference
	 * @return [Promise] promise that will resolve templates
	 */
	elementRefsToTemplates: function(elementRefs) {

		return new Promise(function(resolve, reject) {
			if (!elementRefs) {
				return reject();
			}

			if (!window.__adobe_cep__) {
				return resolve(iaw.fnftFakeLicensedTemplates);
			}

			function onFinish(templates) {
				resolve(templates);
			}

			if (!iaw.libraryManager.isLibraryCollelctionsLoaded()) {
				iaw.log.console('ElementRefs to templates: Fail to load library collections.');
				return reject();
			}

			var callCount = 0;
			var elements = [];
			elementRefs.forEach(function(elementRef) {
				if (!elementRef) return;
				elements.push(ccLibraries.resolveElementReference(elementRef));
			});

			var templates = [];
			if (!elements || elements.length === 0) {
				return onFinish([]);
			}
			elements.forEach(function(element) {
				// build up template obj.
				iaw.libraryManager.buildupTemplate(element)
				.then(function(template) {
					templates.push(template);
					if (++callCount === elements.length) {
						onFinish(templates);
					}
				})
				.catch(function() {
					if (++callCount === elements.length) {
						onFinish(templates);
					}
				});
			});
		});
	},

	/**
	 * Get template path per library Id.
	 *
	 * @param [Object] libraryCollection	library collection
	 * @param [String] stockTemplateId		stock template id
	 * @return [Promise] promise that will resolve path of template
	 */
	getTemplatePath: function(libraryCollection, stockTemplateId) {
		return new Promise(function(resolve, reject) {
			if (!window.__adobe_cep__) {
				var path;
				iaw.fnftFakeLicensedTemplates.forEach(function(template) {
					if (template.id === stockTemplateId) {
						path = template.url;
					}
				});
				return path;
			}
			var isFound = false;
			libraryCollection.libraries.forEach(function(library) {
				library.elements.forEach(function(element) {
					var id = iaw.stockUtil.getElementStockId(element);
					if (id === stockTemplateId && !isFound) {
						isFound = true;
						element.getPrimaryRepresentation().getContentPath(function(err, path) {
							resolve(path);
						});
					}
				});
			});
			if (!isFound) {
				resolve();
			}
		});
	},

	/**
	 * Check if temlate is licensed or not.
	 *
	 * @param [String] id	template id
	 * @return [Boolean] true or false.
	 */
	isTemplateLicensed: function(id) {
		if (!window.__adobe_cep__) {
			var license = false;
			iaw.fnftFakeLicensedTemplates.forEach(function(template) {
				if (template.id === id) {
					license = true;
				}
			});
			return license;
		}

		if (iaw.libraryManager.cachedLicensedTemplates && Object.keys(iaw.libraryManager.cachedLicensedTemplates).length > 0) {
			if (iaw.libraryManager.cachedLicensedTemplates[id] !== undefined) {
				return true;
			}
		}
		return iaw.libraryManager.statusLookupMap[id] && (iaw.libraryManager.statusLookupMap[id].downloadStatus === iaw.libraryManager.DOWNLOADED);
	},

	/**
	 * Check if temlate is in downloading.
	 *
	 * @param [String] id	template id
	 * @return [Boolean] true or false.
	 */
	isTemplateDownloading: function(id) {
		return iaw.libraryManager.statusLookupMap[id] && (iaw.libraryManager.statusLookupMap[id].downloadStatus === iaw.libraryManager.DOWNLOADING);
	},

	/**
	 * Get the status of template.
	 *
	 * @param [String] id	template id
	 * @return [Object] template status.
	 */
	getTemplateStatus: function(id) {
		return iaw.libraryManager.statusLookupMap[id];
	},

	/**
	 * Set status for template.
	 *
	 * @param [String] id		template id
	 * @param [Object] status	template status
	 */
	setTemplateStatus: function(id, status) {
		return iaw.libraryManager.statusLookupMap[id] = status;
	},

	/**
	 * Get element reference by template Id
	 *
	 * @param [String] id	template id
	 * @return [String] element reference
	 */
	getElementRefById: function(id) {
		return iaw.libraryManager.statusLookupMap[id] && iaw.libraryManager.statusLookupMap[id].elementRef;
	},

	/**
	 * Get template Id by element reference
	 *
	 * @param [String] elementRef element reference
	 * @return [String] template id
	 */
	getIdByElementRef: function(elementRef) {
		if (typeof ccLibraries !== 'undefined') {
			var element = ccLibraries && ccLibraries.resolveElementReference(elementRef);
			return element && iaw.stockUtil.getElementStockId(element);
		}
		return null;
	},

	/**
	 * Check if cc library collections get loaded
	 *
	 * @return [Boolean] true or false.
	 */
	isLibraryCollelctionsLoaded: function() {
		return (iaw.libraryManager.libraryCollections && iaw.libraryManager.libraryCollections.length > 0);
	},

	/**
	 * Get the cached licensed templates using the lookup file.
	 *
	 * @param [String] hostID	host id, like 'PHXS'
	 * @return [Promise] promise that will resolve licensed templates
	 */
	getCachedTemplates: function(hostID, resolve) {
		if (iaw.fnftFakeLicensedTemplates) {
			var templates = [];
			iaw.fnftFakeLicensedTemplates.forEach(function(template) {
				if (template['mime_type'] === iaw.libraryManager.MIME_TYPE[hostID]) {
					templates.push(template);
					iaw.libraryManager.cachedLicensedTemplates[template.id] = template;
				}
			});
			return resolve(templates);
		}

		var licensedTemplates = [];
		var csInterface = new CSInterface();
		var lookupFilePath = csInterface.getSystemPath(SystemPath.USER_DATA) + '/Adobe/Creative Cloud Libraries/LIBS/librarylookupfile';
		var lookupMapJSON = iaw.json.readLocalJSONFile(lookupFilePath);

		if (lookupMapJSON && lookupMapJSON.libraries) {

			Object.keys(lookupMapJSON.libraries).forEach(function(libId) {
				var lib = lookupMapJSON.libraries[libId];
				if (!lib || !lib.elements) return;

				Object.keys(lib.elements).forEach(function(elementId) {
					var element = lib.elements[elementId];
					if (!element || !element.reps) return;

					var template = {
						id: element.stockId,
						template_category: ['saved', 'recent'],
						title: element.name,
						created: element.mod, // no creation time so set modified time
						licensedTime: element.mod,
						modified: element.mod,
						lastUsedTime: iaw.localstorage.getUserItem('templateLUT_' + element.stockId) || element.mod,
						description: iaw.i18n.getLocalizedString('newdoc_details_template_default'),
						elementRef: 'cloud-asset://' + lib.domain + lib.path + '/' + libId + ';node=' + elementId,
						previews: []
					};

					var isWindows = iaw.store.get(['host', 'platform']) === 'win' || iaw.store.get(['host', 'platform']) === 'windows' || false;
					element.reps.forEach(function(rep) {
						if (rep && (rep.type === iaw.libraryManager.MIME_TYPE[hostID])) {
							template['mime_type'] = rep.type;
							if (isWindows) {
								template.url = rep.path.replace(/\\/g, '/');
							}
							else {
								template.url = rep.path;
							}
						}
						else if (rep && (rep.type === 'image/jpeg')) {
							if (isWindows) {
								template['thumbnail_url'] = rep.path.replace(/\\/g, '/');
							}
							else {
								template['thumbnail_url'] = rep.path;
							}
						}
					});

					if (template['mime_type'] && template.url) {
						licensedTemplates.push(template);
					}
				});
			});
		}

		licensedTemplates.forEach(function(template) {
			iaw.libraryManager.cachedLicensedTemplates[template.id] = template;
		});

		resolve(licensedTemplates);
	},

	/**
	 * Get template id from elementRef of cached templates.
	 *
	 * @param [String] elementRef element reference
	 * @return [String] template id
	 */
	getIdByCachedElementRef: function(elementRef) {
		var templates = iaw.libraryManager.cachedLicensedTemplates;
		var keys = Object.keys(templates);

		if (templates && keys.length > 0) {
			for (var i=0; i<keys.length; i++) {
				var key = keys[i];
				if (templates[key].elementRef === elementRef) {
					return templates[key].id;
				}
			}
		}
		return null;
	}
};
